import {Box} from '@mui/material';
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import {Loading} from '@wandb/weave/components/Loading';
import {SmallRef} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/smallRef/SmallRef';
import {parseRefMaybe} from '@wandb/weave/react';
import React, {FC, useState} from 'react';

export const ALL_DATASETS_OPTION = '__all__' as const;

interface CompareEvaluationsDropdownButtonProps {
  entity: string;
  project: string;
  selectedEvaluations: string[];
  onDatasetSelect: (datasetId: string) => void;
  availableDatasets?: Array<{
    id: string;
    name: string;
    datasetName: string;
    datasetVersion: string;
  }>;
  disabled?: boolean;
  loading?: boolean;
}

export const CompareEvaluationsDropdownButton: FC<
  CompareEvaluationsDropdownButtonProps
> = ({
  entity,
  project,
  selectedEvaluations,
  onDatasetSelect,
  availableDatasets = [],
  disabled,
  loading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonText = selectedEvaluations.length === 1 ? 'View' : 'Compare';
  const isSingleEvaluation = selectedEvaluations.length === 1;

  // For single evaluation, directly navigate to first available dataset
  const handleSingleEvaluationClick = () => {
    if (availableDatasets.length > 0) {
      onDatasetSelect(availableDatasets[0].name);
    }
  };

  // If there's only one dataset, show a simple button instead of dropdown
  if (availableDatasets.length === 1) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}>
        <Button
          size="medium"
          variant="primary"
          disabled={disabled || loading}
          icon="chart-scatterplot"
          onClick={() => onDatasetSelect(availableDatasets[0].name)}
          tooltip="Compare evaluations">
          <div className="flex items-center gap-2">
            {loading ? <Loading size={16} /> : buttonText}
          </div>
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      {isSingleEvaluation ? (
        // Direct button for single evaluation
        <Button
          size="medium"
          variant="primary"
          disabled={disabled || loading || availableDatasets.length === 0}
          icon="chart-scatterplot"
          onClick={handleSingleEvaluationClick}
          tooltip="View evaluation">
          <div className="flex items-center gap-2">
            {loading ? <Loading size={16} /> : buttonText}
          </div>
        </Button>
      ) : (
        // Dropdown for multiple evaluations
        <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenu.Trigger asChild>
            <Button
              size="medium"
              variant="primary"
              disabled={disabled || loading}
              icon="chart-scatterplot"
              active={isOpen}
              tooltip="Select dataset to compare evaluations">
              <div className="flex items-center gap-2">
                {loading ? (
                  <Loading size={16} />
                ) : (
                  <>
                    {buttonText}
                    <Icon width={16} height={16} name="chevron-down" />
                  </>
                )}
              </div>
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal container={document.body}>
            <DropdownMenu.Content
              align="start"
              sideOffset={4}
              className="min-w-[200px]"
              style={{zIndex: 20}}>
              {availableDatasets.length === 0 ? (
                <DropdownMenu.Item disabled>
                  <div className="flex items-center gap-2 text-moon-500">
                    <Icon name="info" width={16} height={16} />
                    No datasets available
                  </div>
                </DropdownMenu.Item>
              ) : (
                <>
                  {/* Decorative subheader */}
                  <DropdownMenu.Item disabled>
                    <div className="py-0.5 px-2 text-sm font-medium text-moon-500">
                      Compare evals in:
                    </div>
                  </DropdownMenu.Item>

                  {/* Dataset options */}
                  {availableDatasets.map(dataset => (
                    <DropdownMenu.Item
                      key={dataset.name}
                      onClick={() => {
                        onDatasetSelect(dataset.name);
                        setIsOpen(false);
                      }}>
                      <div className="flex items-center gap-2">
                        {(() => {
                          // Construct ref with version if we have it
                          const refString = dataset.name.startsWith('weave:///')
                            ? dataset.name
                            : `weave:///${entity}/${project}/object/${dataset.datasetName}:${dataset.datasetVersion}`;
                          const ref = parseRefMaybe(refString);
                          return ref ? (
                            <SmallRef
                              objRef={ref}
                              iconOnly={false}
                              noLink={true}
                            />
                          ) : (
                            <span>{dataset.name}</span>
                          );
                        })()}
                      </div>
                    </DropdownMenu.Item>
                  ))}
                  <DropdownMenu.Separator />
                  {/* Compare all evaluations option */}
                  <DropdownMenu.Item
                    onClick={() => {
                      onDatasetSelect(ALL_DATASETS_OPTION);
                      setIsOpen(false);
                    }}>
                    <div className="flex items-center gap-2">
                      <Icon name="chart-scatterplot" width={16} height={16} />
                      <span className="ml-6 font-semibold">
                        Compare all evaluations
                      </span>
                    </div>
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </Box>
  );
};
